/**
 * BookCard component with 3D book mockup and spine effect.
 * Features entrance animation, dynamic state with webm background,
 * and scroll-tethered exit fade animation.
 * Based on Figma design for Books media type.
 */

import React, { FC, useRef, useEffect } from 'react';
import { styled } from 'linaria/react';

import booksBgVideo from '../../../assets/videos/books_bg.webm';
import { SCROLL_IDLE_MS } from '../../../constants/animation';
import { useScrollExitAnimation } from '../../../hooks/useScrollExitAnimation';
import { BookCardProps } from '../types';
import { BORDER_RADIUS, SHADOWS, DIMENSIONS, CARD_ENTRANCE } from '../styled/tokens';

/* ─── tokens ─── */

const BOOK_DYNAMIC = {
  /** Video background dimensions (from Figma DS frame: 390×480) */
  bgWidth: 390,
  bgHeight: 480,
  /** Video layer opacity */
  bgOpacity: 0.45,
  /** Transition duration for fade-in/out */
  bgFadeDuration: 0.8,
} as const;

/* ─── styled components ─── */

const StyledBookContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0 32px;
  overflow: visible;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
`;

const StyledBookWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

/* ─── dynamic state video background ─── */

const StyledVideoBackground = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${BOOK_DYNAMIC.bgWidth}px;
  height: ${BOOK_DYNAMIC.bgHeight}px;
  transform: translate(-50%, -50%);
  z-index: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity ${BOOK_DYNAMIC.bgFadeDuration}s ease;
  overflow: hidden;
  border-radius: 50%;
`;

const StyledBgVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* ─── book cover ─── */

const StyledSpine = styled.div`
  position: absolute;
  left: 11px;
  top: 0;
  width: ${DIMENSIONS.book.spineWidth}px;
  height: 100%;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 17.39%, rgba(26, 29, 55, 0.07) 57.72%, rgba(0, 0, 0, 0) 101.84%);
  border-radius: ${BORDER_RADIUS.sm}px 0 0 ${BORDER_RADIUS.sm}px;
  z-index: 2;
`;

/**
 * Plain transform wrapper — receives the exit rotation/scale transform.
 * Has NO visual styling (no overflow, border-radius, box-shadow).
 * This separation is critical: transforming an element with overflow:hidden +
 * border-radius forces the GPU to recalculate clip/mask regions every frame,
 * causing compositing flicker. By keeping transforms on a plain wrapper,
 * StyledCover's clip region is stable and the whole unit transforms as one.
 */
const StyledExitTransformWrap = styled.div`
  transform-origin: center center;
`;

const StyledCover = styled.div`
  position: relative;
  border-radius: ${BORDER_RADIUS.sm}px;
  overflow: hidden;
  box-shadow: ${SHADOWS.bookCoverShadow};
`;

const StyledCoverImage = styled.img`
  display: block;
  max-width: ${DIMENSIONS.book.maxWidth}px;
  max-height: ${DIMENSIONS.book.maxHeight}px;
  width: auto;
  height: auto;
`;

const StyledCoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  box-shadow: ${SHADOWS.bookInnerShadow};
  pointer-events: none;
`;

const BookCard: FC<BookCardProps> = ({
  title,
  imageUrl,
  backgroundColor,
  externalLink,
  className,
  disableAnimation,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoBgRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const entranceFiredRef = useRef(false);

  const { exitRef } = useScrollExitAnimation({
    disabled: disableAnimation,
  });

  // One-shot entrance: observe intersection + scroll idle, fire once, then disconnect.
  // No React state changes after entrance completes — zero re-renders during scroll.
  useEffect(() => {
    if (disableAnimation) {
      // When disabled, show immediately
      if (wrapperRef.current) {
        wrapperRef.current.style.opacity = '1';
        wrapperRef.current.style.transform = 'translateY(0) scale(1)';
      }
      return undefined;
    }

    const element = containerRef.current;
    const wrapper = wrapperRef.current;
    if (!element || !wrapper) return undefined;

    // Set initial entrance state via DOM (no styled-component props)
    wrapper.style.opacity = String(CARD_ENTRANCE.contentOpacity);
    wrapper.style.transform = `translateY(${CARD_ENTRANCE.contentOffsetY}px) scale(${CARD_ENTRANCE.contentInitialScale})`;
    wrapper.style.transition = 'none';

    let isVisible = false;
    let isScrollIdle = true;
    let scrollTimeoutId: ReturnType<typeof setTimeout>;
    let observer: IntersectionObserver;
    let handleScroll: () => void;

    const fireEntrance = () => {
      if (entranceFiredRef.current) return;
      if (!(isVisible && isScrollIdle)) return;

      entranceFiredRef.current = true;

      // Apply entrance transition via DOM
      wrapper.style.transition = `opacity ${CARD_ENTRANCE.opacityDuration}s ${CARD_ENTRANCE.easing}, transform ${CARD_ENTRANCE.scaleDuration}s ${CARD_ENTRANCE.easing}`;
      wrapper.style.opacity = '1';
      wrapper.style.transform = 'translateY(0) scale(1)';

      // After entrance transition finishes, remove ALL inline styles.
      // The identity transform `translateY(0) scale(1)` creates a stacking
      // context + compositing layer. During fast scroll the compositor shows
      // stale snapshots of this layer → visible flicker. Clearing inline
      // styles returns the wrapper to its static CSS (no transform/opacity)
      // so the browser doesn't maintain a separate compositing layer.
      const cleanupDuration = Math.max(CARD_ENTRANCE.opacityDuration, CARD_ENTRANCE.scaleDuration) * 1000 + 50;
      setTimeout(() => {
        wrapper.style.transition = '';
        wrapper.style.opacity = '';
        wrapper.style.transform = '';
      }, cleanupDuration);

      // After entrance duration, start dynamic phase (video bg)
      setTimeout(() => {
        if (videoBgRef.current) {
          videoBgRef.current.style.opacity = String(BOOK_DYNAMIC.bgOpacity);
        }
        const video = videoRef.current;
        if (video) {
          video.play().catch(() => {});
        }
      }, CARD_ENTRANCE.scaleDuration * 1000);

      // Disconnect all listeners — no more state changes ever
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };

    handleScroll = () => {
      isScrollIdle = false;
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(() => {
        isScrollIdle = true;
        fireEntrance();
      }, SCROLL_IDLE_MS);
    };

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisible = entry.isIntersecting;
          fireEntrance();
        });
      },
      { threshold: 0.7 }
    );

    observer.observe(element);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, [disableAnimation]);

  const bookContent = (
    <StyledBookWrapper ref={wrapperRef}>
      {/* Dynamic state: webm video background behind book */}
      <StyledVideoBackground ref={videoBgRef}>
        <StyledBgVideo ref={videoRef} src={booksBgVideo} muted loop playsInline preload="auto" />
      </StyledVideoBackground>

      {/* Exit transform wrapper — plain div, no visual styling.
          Transform is applied here to avoid GPU clip/mask recalculation
          on StyledCover's overflow:hidden + border-radius. */}
      <StyledExitTransformWrap ref={exitRef}>
        <StyledCover>
          {imageUrl ? (
            <StyledCoverImage src={imageUrl} alt={title} loading="lazy" />
          ) : (
            <div
              style={{
                width: DIMENSIONS.book.maxWidth,
                height: DIMENSIONS.book.maxHeight,
                backgroundColor: backgroundColor ?? '#e8e4dc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                textAlign: 'center',
                fontFamily: "'CaslonIonic', serif",
                fontSize: 18,
                color: 'var(--card-text-primary, #181800)',
              }}
            >
              {title}
            </div>
          )}
          {/* TODO: re-enable once flicker is resolved
          <StyledCoverOverlay />
          <StyledSpine />
          */}
        </StyledCover>
      </StyledExitTransformWrap>
    </StyledBookWrapper>
  );

  return (
    <StyledBookContainer className={className} ref={containerRef}>
      {externalLink ? (
        <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
          {bookContent}
        </StyledLink>
      ) : (
        bookContent
      )}
    </StyledBookContainer>
  );
};

export default BookCard;
